import * as React from "react";
import { UitkCard, UitkCardImageLayout, UitkCardLink } from "uitk-react-cards";
import { NeighborhoodCardProps } from "./typings";
import { NeighborhoodCardMedia } from "./NeighborhoodCardMedia";
import { DesktopCardContent, MobileCardContent } from "./NeighborhoodCardContent";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { inject } from "mobx-react";
import { formatText } from "bernie-l10n";
import { UitkFigureAspectRatioType } from "uitk-react-images";
import { renderScrimAttributionTextHTML } from "./utils/renderScrimAttributionTextHTML";

const localizeKnownForLabel = (affinities: string) =>
  formatText("destinationNeighborhood.topNeighborhoods.knownFor", affinities);

const getSpecialComponent = (children: React.ReactNode) => {
  const components: { [key: string]: React.ComponentElement<any, any> | null } = {};

  React.Children.map(children, (child: React.ComponentElement<any, any>) => {
    switch (child.type.displayName) {
      case "NeighborhoodCardFooter":
        components[child.type.displayName] = child;
    }
  });

  return components;
};

export const NeighborhoodCard: React.FC<NeighborhoodCardProps> = inject("thumbor")(props => {
  const className = props.className || "";
  const { id, link, media, region, affinities } = props.neighborhood;

  const name: string = region && region.name;
  const description: string = region && region.description;

  /** Concatenate affinities into a single string (e.g. beaches, surfing, seafood)  */
  const neighborhoodAffinities: string = affinities.reduce((result, affinity) => {
    if (result === "") {
      return affinity.name;
    }

    return `${result}, ${affinity.name}`;
  }, "");

  const averageRating =
    props.neighborhood.aggregateReviewsAndRatings &&
    props.neighborhood.aggregateReviewsAndRatings.averageRating;
  const totalRatings =
    props.neighborhood.aggregateReviewsAndRatings &&
    props.neighborhood.aggregateReviewsAndRatings.totalRatings;

  const { NeighborhoodCardFooter: CardFooter } = getSpecialComponent(props.children);

  const defaultSubTitle = neighborhoodAffinities && localizeKnownForLabel(neighborhoodAffinities);
  const containerClasses = `destinationNeighborhoodsCard uitk-cell ${className}`;

  return props.isDesktop ? (
    <div className={containerClasses}>
      <UitkCard className="uitk-flex-column fullHeight" withBorder={Boolean(props.withBorder)}>
        <NeighborhoodCardMedia
          id={id}
          regionName={name}
          media={media}
          context={props.context}
          showFavorite={false}
          ratio={UitkFigureAspectRatioType.R21_9}
          renderAttributionText={renderScrimAttributionTextHTML}
        />
        <DesktopCardContent
          title={name}
          subTitle={props.neighborhood.infoPois}
          description={description}
          averageRating={averageRating}
          totalRatings={totalRatings}
          context={props.context}
        />
        {CardFooter}
        {props.isClickeable && (
          <TrackedLink
            moduleName="DestinationNeighborhoods"
            href={link}
            rfrr={"TG.LP.Dest.Neighborhoods"}
            className="uitk-card-link"
          >
            {name}
          </TrackedLink>
        )}
      </UitkCard>
    </div>
  ) : (
    <div className="destinationNeighborhoodsCard all-b-margin-one">
      <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}>
        <UitkCardLink>
          <a href={link}>{name}</a>
        </UitkCardLink>
        <NeighborhoodCardMedia
          id={id}
          regionName={name}
          media={media}
          context={props.context}
          showFavorite={props.showFavorite}
        />
        <MobileCardContent title={name} subTitle={defaultSubTitle} />
      </UitkCard>
    </div>
  );
});

export const NeighborhoodCardFooter: React.FC = props => (
  <div className="uitk-card-content destinationNeighborhoodsCardFooter">{props.children}</div>
);
NeighborhoodCardFooter.displayName = "NeighborhoodCardFooter";

export default NeighborhoodCard;
